import { ROUTES } from '~/constants/routes'

/** §1 route-family URL state contract (spec 2026-07-31): a URL hosts only the keys its
 *  route family owns. This module IS the written contract — new params must be registered
 *  here or they get stripped on pin/neutral routes (that is the contract working, loudly). */
export type RouteFamily = 'review' | 'pin' | 'neutral'

/** Named Review page-state keys (census 2026-07-31). */
const PAGE_STATE_KEYS = new Set([
  'tf_preset',
  'from',
  'to',
  'tf_list',
  'compareMode',
  'compare',
  'compareKpi',
  'compareFrom',
  'compareTo',
  'kpi_root',
  'kpi_driver',
  'kpi_value_type',
  'groupby',
  'columns',
  'value_types',
  'gran_unit',
  'gran_dots',
  'panel_base',
  'campaign',
  'view',
])

const PIN_KNOB_PREFIX = 'c_pin:'

/** Prefix match at a path-segment boundary (07-31 ledger A1): '/review-status/x' matches
 *  '/review-status'; '/review-statusfoo' does not — a lookalike path gets NO family. */
function startsWithSegment(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(route + '/')
}

export function isPinKnobKey(key: string): boolean {
  return key.startsWith(PIN_KNOB_PREFIX)
}

/** Review page state = named keys + `dim.*` + every `c_*` that is NOT a pin knob. */
export function isPageStateKey(key: string): boolean {
  if (isPinKnobKey(key)) return false
  return PAGE_STATE_KEYS.has(key) || key.startsWith('dim.') || key.startsWith('c_')
}

/** Family of a pathname; null = unfamilied route (login/callback/unknown) — callers MUST
 *  no-op there (never strip an auth callback's `code`/`state`). */
export function routeFamilyOf(pathname: string): RouteFamily | null {
  if (pathname === ROUTES.home || startsWithSegment(pathname, ROUTES.collections)) return 'pin'
  if (
    startsWithSegment(pathname, ROUTES.reviewStatus) ||
    startsWithSegment(pathname, ROUTES.reviewPerformance)
  )
    return 'review'
  if (startsWithSegment(pathname, ROUTES.profile) || startsWithSegment(pathname, ROUTES.account))
    return 'neutral'
  return null
}

/** Keys to DELETE from `params` for a family. Review returns [] — its `c_pin:*` cleanup
 *  lives in useReviewPageState's arrival reconcile (single-writer doctrine there). */
export function foreignKeysFor(family: RouteFamily, params: URLSearchParams): string[] {
  if (family === 'review') return []
  const foreign = new Set<string>()
  for (const key of params.keys()) {
    if (family === 'neutral' || !isPinKnobKey(key)) foreign.add(key)
  }
  return [...foreign]
}
